/*
 * <editorial-tabs> — the tabbed variant of the Editorial Story section.
 *
 * Progressive enhancement: the markup ships every panel visible and stacked, and the
 * CSS only collapses them into one stack once this element is defined, so a failed
 * script leaves the chapters readable instead of hiding five of six.
 *
 * Follows the WAI-ARIA tabs pattern: one tab in the tab order at a time (roving
 * tabindex), arrow keys move between tabs (mirrored under RTL), Home/End jump to the
 * ends, and activation follows focus.
 */
class EditorialTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    // Round previous / next buttons beside the open chapter. The ends disable instead of
    // wrapping, so "next" on the last chapter does not jump back to the first.
    this.prevButton = this.querySelector('[data-tabs-prev]');
    this.nextButton = this.querySelector('[data-tabs-next]');
    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));

    const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.select(selected === -1 ? 0 : selected);
  }

  select(index, moveFocus = false) {
    this.index = index;
    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      tab.classList.toggle('is-active', isActive);
      if (this.panels[i]) this.panels[i].classList.toggle('is-active', isActive);
    });
    if (moveFocus) this.tabs[index].focus();
    this.setArrow(this.prevButton, index === 0);
    this.setArrow(this.nextButton, index === this.tabs.length - 1);
    this.revealTab(this.tabs[index]);
  }

  step(delta) {
    const next = this.index + delta;
    if (next < 0 || next > this.tabs.length - 1) return;
    this.select(next);
  }

  setArrow(button, atEnd) {
    if (!button) return;
    button.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    button.tabIndex = atEnd ? -1 : 0;
  }

  // Scroll the tab row sideways (only sideways: the page must not jump) so the open
  // chapter's pill is in view when the arrows drive the selection on a narrow screen.
  revealTab(tab) {
    const list = tab.closest('[role="tablist"]');
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const target = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    const forward = document.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = document.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    let next;

    if (event.key === forward) next = index === last ? 0 : index + 1;
    else if (event.key === back) next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;

    event.preventDefault();
    this.select(next, true);
  }
}

if (!customElements.get('editorial-tabs')) customElements.define('editorial-tabs', EditorialTabs);
